import {
    DataEntryUIControlType,
    FormConfiguration,
    FormConfigurationComplexOptionDefinition,
    FormConfigurationCopySettings,
    FormConfigurationDataset,
    FormConfigurationDatasetAddActionCondition,
    FormConfigurationDatasetAddActionLabelOverride,
    FormConfigurationElementControlBase,
    FormConfigurationElementControlType,
    FormConfigurationElementDatasetManager,
    FormConfigurationElementOptionControl,
    FormConfigurationElementOptionControlType,
    FormConfigurationElementSimpleControlType,
    FormConfigurationElementType,
    FormConfigurationElementTypeMapper,
    FormConfigurationImageDefinition,
    FormConfigurationLocalizedText,
    FormConfigurationOptionsDefinition,
    FormConfigurationOptionsDefinitionDynamic,
    FormConfigurationOptionsDirection,
    FormConfigurationOptionsType,
    FormConfigurationMergedSubjectsView,
    FormConfigurationSubjectSubgroup,
    SubjectLabelPartType
} from "@fhir-api";
import {
    AttributeDataTypeEnum,
    ReferenceDataType,
    Scm,
    ScmEntityAttribute,
    ScmEntityAttributeEnumExtension,
    ScmEntityContent
} from "@logex/expression-validator";
import { computed, inject, Injectable, Signal, signal } from "@angular/core";
import { AppProjectsService } from "../app-projects.service";
import { DatePipe } from "@angular/common";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { catchError, combineLatest, filter, map, Observable, of, switchMap, take, tap } from "rxjs";
import {
    CategoryPath,
    DataPath,
    SectionPath,
    VariablePath
} from "./app-navigation-manager.service";
import { hasOwnProperty } from "@microsoft/applicationinsights-core-js";
import { AppProjectTranslateService } from "../localization/app-project-translate.service";
import decodeHtmlEntities from "@app/util/html-util";
import { LgTranslateService } from "@logex/framework/lg-localization";
import { pickKeys, replaceUndefinedValues } from "@app/util/object-util";
import {
    addVariable,
    apiPathToName,
    pathDatasetName,
    subjectToPath
} from "@app/util/data-path-util";
import { SubjectInstance } from "./app-subject-manager.service";
import { AppImageManagerService } from "./app-image-manager.service";
import { AppSubjectApiService } from "./app-subject-api.service";
import { buildFormConfigurationFieldLookup } from "./form-configuration-field-lookup.util";

export interface FieldDefinition<
    T extends FormConfigurationElementType | undefined = undefined,
    V extends FormConfigurationElementControlType | undefined = undefined
> {
    formConfiguration: FormConfigurationElementTypeMapper<T, V>;
}

export interface VariableDefinition<
    T extends FormConfigurationElementControlType | undefined = undefined
> extends FieldDefinition<FormConfigurationElementType.Control, T> {
    scm: ScmEntityAttribute;
    length?: number;
}

export interface DatasetDefinition {
    formConfiguration: FormConfigurationDataset;
    scm: ScmEntityContent;
}

export interface ImageDefinition {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
    disabled?: boolean;
    title?: string;
    titleColor?: string;
    tooltip?: string;
    dotColor?: string;
}

export interface SubjectVariableOptionset {
    options: SubjectVariableOption[];
}

export interface SubjectVariableOption {
    value: string;
    label: string;
    description?: string;
    image?: ImageDefinition;
}

export const DEFAULT_BOOLEAN_OPTIONS: SubjectVariableOption[] = [
    {
        value: "true",
        label: "Ja",
        description: "Ja"
    },
    {
        value: "false",
        label: "Nee",
        description: "Nee"
    }
];

export interface CategoryData {
    category: string;
    section: string;
}

interface FormConfigurationLabelIndex {
    sections: Record<string, FormConfigurationLocalizedText[]>;
    categories: Record<string, Record<string, FormConfigurationLocalizedText[]>>;
}

@Injectable({
    providedIn: "root"
})
export class AppDefinitionManagerService {
    private _projectsService = inject(AppProjectsService);
    private _scm = this._projectsService.scm;
    private _formConfiguration = this._projectsService.formConfiguration;
    private _projectTranslateService = inject(AppProjectTranslateService);
    private _translateService = inject(LgTranslateService);
    private _imageManagerService = inject(AppImageManagerService);
    private _appSubjectApiService = inject(AppSubjectApiService);
    private _datePipe = new DatePipe("nl-NL");
    private _scmInstanceNameMapper = signal<Record<string, string> | null>(null);
    private _scmEntityModelNameMapper = signal<Record<string, string> | null>(null);
    private _scmEntityLookup = signal<Record<string, ScmEntityContent> | null>(null);
    private _scmAttributeLookup = signal<Record<string, Record<string, ScmEntityAttribute>> | null>(
        null
    );
    private _formConfigurationLookup = computed<Record<string, FormConfigurationDataset> | null>(
        () => {
            const formConfiguration = this._formConfiguration();
            if (!formConfiguration) return null;
            const lookup: Record<string, FormConfigurationDataset> = {};
            for (const dataset of formConfiguration.datasets) {
                lookup[dataset.name] = dataset;
            }
            return lookup;
        }
    );
    private _fieldDefinitionLookup = computed(() => {
        const formConfigurations = this._formConfigurationLookup();
        return formConfigurations ? buildFormConfigurationFieldLookup(formConfigurations) : null;
    });
    private _formConfigurationLabelLookup = computed(() => {
        const formConfigurations = this._formConfigurationLookup();
        if (!formConfigurations) {
            return null;
        }

        const lookup = Object.create(null) as Record<string, FormConfigurationLabelIndex>;
        for (const [datasetName, dataset] of Object.entries(formConfigurations)) {
            const sections = Object.create(null) as Record<
                string,
                FormConfigurationLocalizedText[]
            >;
            const categories = Object.create(null) as Record<
                string,
                Record<string, FormConfigurationLocalizedText[]>
            >;

            for (const section of dataset.sections) {
                if (!Object.hasOwn(sections, section.name)) {
                    sections[section.name] = section.label;
                }
                const sectionCategories = (categories[section.name] ??= Object.create(null));
                for (const category of section.categories) {
                    if (!Object.hasOwn(sectionCategories, category.name)) {
                        sectionCategories[category.name] = category.label ?? [];
                    }
                }
            }

            lookup[datasetName] = { sections, categories };
        }

        return lookup;
    });
    private _scmLoaded = signal(false);
    private _formConfigurationLoaded = computed(() => !!this._formConfigurationLookup());
    definitionsLoaded = computed(() => this._scmLoaded() && this._formConfigurationLoaded());

    _palgaEnabled = computed(
        () => this._projectsService.selectedProject()?.properties.isPalgaEnabled ?? false
    );

    private _imageDefinitions = new Map<string, ImageDefinition | null>();

    constructor() {
        toObservable(this._scm)
            .pipe(
                filter(scm => !!scm),
                tap(scm => {
                    this._generateScmEntityLookup(scm);
                    this._scmLoaded.set(true);
                })
            )
            .subscribe();

        toObservable(this._formConfiguration)
            .pipe(
                filter(formConfiguration => !!formConfiguration),
                switchMap(formConfiguration => {
                    this._imageDefinitions.clear();
                    const imageRequests = (formConfiguration.resources ?? []).map(resource =>
                        this._getImageDefinition$(resource.src).pipe(
                            catchError(error => {
                                console.error(`Failed to cache image ${resource.src}`, error);
                                return of(null);
                            })
                        )
                    );

                    return imageRequests.length > 0 ? combineLatest(imageRequests) : of([]);
                })
            )
            .subscribe();
    }

    getTaskIds(): string[] {
        return this._formConfiguration()?.tasks?.map(task => task.task_id) ?? [];
    }

    getTaskLabel(taskId: string): string {
        const taskLabel = this._formConfiguration()?.tasks?.find(
            task => task.task_id === taskId
        )?.labels;
        if (taskLabel === undefined) {
            return taskId;
        }
        return this._projectTranslateService.handleLocalizedText(taskLabel ?? []);
    }

    getNavigationPathLabel({
        path,
        subject
    }: {
        path?: CategoryPath;
        subject?: SubjectInstance;
    }): string {
        if (subject) {
            return this.getSubjectLabel(subject);
        } else if (path && path.section) {
            return this.getSectionLabel(path);
        }
        return "-";
    }

    getFieldCategory(path: VariablePath): CategoryData | null {
        const rootDataset = this._projectsService.rootDataset();
        const rootSubject = this._projectsService.rootSubject();
        if (!rootDataset || !rootSubject) {
            throw new Error("No root dataset found");
        }
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }

        this.getDatasetDefinition(pathDatasetName(path));
        const scmInstanceName = this.modelNameToInstanceName(pathDatasetName(path));
        const fieldLocation =
            this._fieldDefinitionLookup()?.[scmInstanceName]?.[path.variable]?.[0];
        if (
            !fieldLocation ||
            fieldLocation.section === undefined ||
            fieldLocation.category === undefined
        ) {
            return null;
        }
        return { category: fieldLocation.category, section: fieldLocation.section };
    }

    /** Returns every form-configuration occurrence of a variable, in configuration order. */
    getFieldDefinitionEntries(path: VariablePath) {
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }
        const scmInstanceName = this.modelNameToInstanceName(pathDatasetName(path));
        return this._fieldDefinitionLookup()?.[scmInstanceName]?.[path.variable] ?? [];
    }

    getFieldIcon(path: VariablePath): Observable<ImageDefinition | null> {
        const fieldDefinition = this.getFieldDefinition(path);
        const imageResourceName = fieldDefinition?.formConfiguration.iconResource;
        if (
            !imageResourceName ||
            (imageResourceName.includes("palga") && this._palgaEnabled() === false)
        ) {
            return of(null);
        }
        return this._getImageDefinition$(imageResourceName).pipe(
            map(imageDefinition => {
                if (!imageDefinition) {
                    return null;
                }

                if (imageResourceName.includes("palga")) {
                    return {
                        ...imageDefinition,
                        title: this._translateService.translate("APP._Variable.Automated_palga"),
                        titleColor: "rgb(var(--control-base-color))"
                    };
                }
                return imageDefinition;
            })
        );
    }

    getCategoryLabel(path: CategoryPath): string {
        this.getDatasetDefinition(pathDatasetName(path));
        const datasetName = this.modelNameToInstanceName(pathDatasetName(path));
        const label =
            this._formConfigurationLabelLookup()?.[datasetName]?.categories[path.section]?.[
                path.category
            ];
        return this._projectTranslateService.handleLocalizedText(label ?? []);
    }

    getCreationLabel(datasetName: string | null): string {
        if (!datasetName) {
            return "";
        }
        const dataset = this.getDatasetDefinition(datasetName);
        const category = dataset.formConfiguration.initializationSection.categories.at(0);
        return this._projectTranslateService.handleLocalizedText(category?.label ?? []);
    }

    getSectionLabel(path: SectionPath): string {
        this.getDatasetDefinition(pathDatasetName(path));
        const datasetName = this.modelNameToInstanceName(pathDatasetName(path));
        const label = this._formConfigurationLabelLookup()?.[datasetName]?.sections[path.section];
        return this._projectTranslateService.handleLocalizedText(label ?? []);
    }

    getDatasetLabel(datasetName: string): string {
        const dataset = this.getDatasetDefinition(datasetName);
        const label = dataset.formConfiguration.addSubjectLabel ?? dataset.formConfiguration.label;
        return this._projectTranslateService.handleLocalizedText(label);
    }

    getSubjectLabel(subject: SubjectInstance): string {
        const dataset = this.getDatasetDefinition(apiPathToName(subject.datasetUri));
        const parts = dataset.formConfiguration.settings?.subjectLabelFormat ?? [];
        if (parts.length === 0) {
            parts.push({
                type: SubjectLabelPartType.Text,
                value: this._projectTranslateService.handleLocalizedText(
                    dataset.formConfiguration.label
                )
            });
            for (const keyVariable of Object.entries(this.getSubjectKeyVariables(subject))) {
                parts.push({
                    type: SubjectLabelPartType.Variable,
                    value: keyVariable[0]
                });
                parts.push({
                    type: SubjectLabelPartType.Text,
                    value: "/"
                });
            }
            if (Object.entries(this.getSubjectKeyVariables(subject)).length > 0) {
                parts.pop();
            }
        }
        const subjectLabel = parts
            .map(part => {
                if (part.type === SubjectLabelPartType.Variable) {
                    return this.getValueLabel(
                        addVariable(subjectToPath(subject), part.value),
                        subject.variable?.[part.value]
                    );
                }
                return part.value;
            })
            .join("");
        return subjectLabel;
    }

    getFieldLabel(path: VariablePath): string {
        const fieldDefinition = this.getFieldDefinition(path);
        if (
            fieldDefinition?.formConfiguration.type === FormConfigurationElementType.CopySubject &&
            !fieldDefinition.formConfiguration.label
        ) {
            return this._translateService.translate("APP._SubjectEdit.CreateFromCopy", {
                dataset: this.getDatasetLabel(pathDatasetName(path))
            });
        }
        return this._projectTranslateService.handleLocalizedText(
            fieldDefinition?.formConfiguration.label ?? []
        );
    }

    getKeyVariables(datasetName: string): string[] {
        const dataset = this.getDatasetDefinition(datasetName);
        return dataset.formConfiguration.initializationSection.categories
            .flatMap(category => category.elements)
            .filter(element => hasOwnProperty(element, "attribute"))
            .map(element => element.name);
    }

    getDataVariables(datasetName: string, sectionName?: string, categoryName?: string): string[] {
        const dataset = this.getDatasetDefinition(datasetName);
        return dataset.formConfiguration.sections
            .filter(section => (sectionName ? section.name === sectionName : true))
            .flatMap(section => section.categories)
            .filter(category => (categoryName ? category.name === categoryName : true))
            .flatMap(category => category.elements)
            .filter(element => {
                return (
                    (element.type === FormConfigurationElementType.Control &&
                        hasOwnProperty(element, "attribute")) ||
                    element.type === FormConfigurationElementType.Setter ||
                    element.type === FormConfigurationElementType.Dataset ||
                    element.type === FormConfigurationElementType.Paragraph
                );
            })
            .map(element => element.name);
    }

    getSubjectKeyVariables(subject: SubjectInstance): Record<string, string | undefined> {
        const keyVariables = this.getKeyVariables(apiPathToName(subject.datasetUri));
        const keyVariablesValues = replaceUndefinedValues(
            pickKeys(subject.variable ?? {}, keyVariables),
            ""
        );
        return keyVariablesValues;
    }

    getValueLabel(path: VariablePath, subjectValue?: string): string {
        const fieldDefinition = this.getFieldDefinition<FormConfigurationElementType.Control>(path);
        if (!fieldDefinition) {
            return "";
        }
        const variableDefinition = this.getVariableDefinition(
            pathDatasetName(path),
            fieldDefinition
        );
        if (!variableDefinition) {
            return "";
        }
        if (!subjectValue || subjectValue === "") {
            return "";
        }
        return this.getFormattedValue(variableDefinition, subjectValue ?? "", true);
    }

    getAddSubjectLabel(path: DataPath, fieldName?: string, wide: boolean = false): string {
        const maxLength = wide ? 60 : 30;
        let label: string;

        if (fieldName) {
            label = this.getDatasetLabel(fieldName).toLowerCase();
        } else {
            label = this.getDatasetLabel(pathDatasetName(path)).toLocaleLowerCase();
        }

        if (label.length > maxLength) {
            const bracketPos = label.indexOf("(");
            if (bracketPos > 0 && bracketPos <= maxLength) {
                label = label.substring(0, bracketPos);
            } else {
                label = "";
            }
        }

        let lc: string;

        if (fieldName) {
            lc = label
                ? "APP._SubjectEdit.Add_sub_subject_button"
                : "APP._SubjectEdit.Add_sub_subject_button_no_label";
        } else {
            lc = label
                ? "APP._SubjectList.Add_subject_button"
                : "APP._SubjectList.Add_subject_button_no_label";
        }

        const addLabel = this._translateService.translate(lc, { label: "" }).trim().toLowerCase();
        const haveLabelAdd = !!addLabel && ` ${label} `.includes(` ${addLabel} `);

        return haveLabelAdd
            ? label.charAt(0).toUpperCase() + label.slice(1)
            : this._translateService.translate(lc, { label });
    }

    isAddButtonVisible(datasetName: string, position: "section" | "navigation"): boolean {
        const visibility =
            this.getDatasetDefinition(datasetName).formConfiguration.addButtonVisibility;
        return visibility == null || visibility[position];
    }

    getSubjectFetchParams(subject: SubjectInstance): Record<string, string> {
        const fetchVariables = replaceUndefinedValues<string, string>(
            this.getSubjectKeyVariables(subject),
            ""
        );
        if (subject.variable?.["uri"]) {
            fetchVariables["uri"] = subject.variable["uri"];
        }
        return fetchVariables;
    }

    getDatasetDefinition(datasetName: string): DatasetDefinition {
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }
        const scmInstanceName = this.modelNameToInstanceName(datasetName);

        const formConfiguration = this._formConfigurationLookup()?.[scmInstanceName ?? datasetName];
        const scm = this._scmEntityLookup()?.[scmInstanceName ?? datasetName];
        if (!formConfiguration) {
            throw new Error("No form configuration found");
        }
        if (!scm) {
            throw new Error("No scm found");
        }
        return {
            formConfiguration: formConfiguration,
            scm: scm
        };
    }

    getDatasetPrimaryKeys(datasetName?: string): string[] {
        const name = datasetName ?? this._projectsService.rootDataset() ?? "";
        const datasetDefinition = this.getDatasetDefinition(name);
        return datasetDefinition.scm.primaryKeys ?? [];
    }

    getFieldDefinition<
        T extends FormConfigurationElementType | undefined = undefined,
        V extends FormConfigurationElementControlType | undefined = undefined
    >(path: VariablePath): FieldDefinition<T, V> | undefined {
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }
        const scmInstanceName = this.modelNameToInstanceName(pathDatasetName(path));
        const fieldDefinition =
            this._fieldDefinitionLookup()?.[scmInstanceName ?? pathDatasetName(path)]?.[
                path.variable
            ]?.[0]?.formConfiguration;
        if (!fieldDefinition) {
            return undefined;
        }
        return {
            formConfiguration: fieldDefinition as FormConfigurationElementTypeMapper<T, V>
        };
    }

    /**
     * Finds the `FormConfigurationElementDatasetManager` element for a child dataset inside
     * the parent dataset's form configuration, searching by `datasetName` (not element `name`).
     * Used by the nav menu where only the child dataset URI is available, not the element name.
     */
    getDatasetManagerElement(
        parentPath: DataPath,
        childDatasetName: string
    ): FormConfigurationElementDatasetManager | undefined {
        if (!this.definitionsLoaded()) return undefined;
        const parentDatasetName = pathDatasetName(parentPath);
        const scmInstanceName = this.modelNameToInstanceName(parentDatasetName);
        const formConfigDataset = this._formConfigurationLookup()?.[scmInstanceName];
        if (!formConfigDataset) {
            return undefined;
        }
        const allElements = [
            ...formConfigDataset.sections.flatMap(s => s.categories).flatMap(c => c.elements),
            ...(formConfigDataset.initializationSection?.categories.flatMap(c => c.elements) ?? [])
        ];
        const found = allElements.find(
            (el): el is FormConfigurationElementDatasetManager =>
                el.type === FormConfigurationElementType.Dataset &&
                el.datasetName === childDatasetName
        );
        return found;
    }

    getVariableDefinition<V extends FormConfigurationElementControlType | undefined = undefined>(
        datasetName: string,
        fieldDefinition: FieldDefinition<FormConfigurationElementType.Control, V>
    ): VariableDefinition<V> | undefined {
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }
        if (!fieldDefinition || !fieldDefinition.formConfiguration.attribute) {
            return undefined;
        }
        const scmInstanceName = this.modelNameToInstanceName(datasetName);
        const scmAttribute =
            this._scmAttributeLookup()?.[scmInstanceName ?? datasetName]?.[
                fieldDefinition.formConfiguration.attribute
            ];
        if (!scmAttribute) {
            return undefined;
        }
        const length =
            scmAttribute?.stringExtension?.maxLength ??
            scmAttribute?.rangeExtension?.maxValue?.toString()?.length;
        return {
            formConfiguration: fieldDefinition.formConfiguration,
            scm: scmAttribute,
            length
        };
    }

    getVariableDataType(path: VariablePath): AttributeDataTypeEnum | null {
        const fieldDefinition = this.getFieldDefinition<FormConfigurationElementType.Control>(path);
        if (!fieldDefinition) {
            return null;
        }
        const variableDefinition = this.getVariableDefinition(
            pathDatasetName(path),
            fieldDefinition
        );
        if (!variableDefinition) {
            return null;
        }

        return variableDefinition.scm.dataType;
    }

    getFormattedValue(
        variableDefinition: VariableDefinition,
        value: string | string[],
        hasOther = false
    ): string {
        if (Array.isArray(value)) {
            return value
                .map(v => this.getFormattedValue(variableDefinition, v, hasOther))
                .join(", ");
        }
        if (variableDefinition.scm?.dataType === AttributeDataTypeEnum.Date) {
            if (value) {
                const prefilledValue = this.checkForPrefillOptions(variableDefinition, value);
                if (prefilledValue != value) {
                    return prefilledValue;
                }
                const dateValue = new Date(value);
                return dateValue && !isNaN(dateValue.getTime())
                    ? (this._datePipe.transform(dateValue, "dd-MM-yyyy") ?? "")
                    : "";
            }
            return "";
        }
        if (variableDefinition.scm?.dataType === AttributeDataTypeEnum.DateTime) {
            if (value) {
                const prefilledValue = this.checkForPrefillOptions(variableDefinition, value);
                if (prefilledValue != value) {
                    return prefilledValue;
                }
                const dateValue = new Date(value);
                return dateValue && !isNaN(dateValue.getTime())
                    ? (this._datePipe.transform(dateValue, "dd-MM-yyyy HH:mm") ?? "")
                    : "";
            }
            return "";
        }
        if (variableDefinition.scm?.dataType === AttributeDataTypeEnum.Timestamp) {
            const date_value = new Date(isNaN(parseInt(value)) ? parseInt(value) : value);
            return date_value && !isNaN(date_value.getTime())
                ? (this._datePipe.transform(date_value, "dd-MM-yyyy HH:mm") ?? "")
                : "";
        }
        if (variableDefinition.scm?.dataType === AttributeDataTypeEnum.Time) {
            if (value) {
                const prefilledValue = this.checkForPrefillOptions(variableDefinition, value);
                if (prefilledValue != value) {
                    return prefilledValue;
                }
                const dateValue = new Date(value);
                return dateValue && !isNaN(dateValue.getTime())
                    ? (this._datePipe.transform(dateValue, "HH:mm") ?? "")
                    : "";
            }
            return "";
        }
        if (hasOwnProperty(variableDefinition.formConfiguration, "optionsDef")) {
            const formConfigurationOptions =
                variableDefinition.formConfiguration as FormConfigurationElementOptionControl;
            if (
                formConfigurationOptions.optionsDef.type ===
                FormConfigurationOptionsType.StaticOptionSet
            ) {
                const option = formConfigurationOptions.optionsDef.options.find(o => {
                    if (variableDefinition.scm.dataType === AttributeDataTypeEnum.Decimal) {
                        return parseFloat(o.value) === parseFloat(value);
                    }
                    return o.value === value;
                });

                if (!option && hasOther) {
                    return value;
                }
                return option
                    ? this._projectTranslateService.handleLocalizedText(option.label)
                    : value;
            }
        }
        return this.checkForPrefillOptions(variableDefinition, value);
    }

    checkForPrefillOptions(variableDefinition: VariableDefinition, value: string): string {
        if (
            variableDefinition.formConfiguration.prefillOptionsDef?.type ===
            FormConfigurationOptionsType.StaticOptionSet
        ) {
            const option = variableDefinition.formConfiguration.prefillOptionsDef.options.find(
                o => {
                    if (variableDefinition.scm.dataType === AttributeDataTypeEnum.Decimal) {
                        return parseFloat(o.value) === parseFloat(value);
                    }
                    return o.value === value;
                }
            );

            return option ? this._projectTranslateService.handleLocalizedText(option.label) : value;
        }
        return value;
    }

    hasOptions(path: VariablePath): boolean {
        const optionset = this._getOptionset(path);
        return (optionset?.scm.enumOptions?.length ?? 0) > 0;
    }

    getOptions(path: VariablePath, specificOptions?: string[]): SubjectVariableOption[] | null {
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }
        const optionset = this._getOptionset(path);
        if (!optionset) {
            const scmInstanceName = this.modelNameToInstanceName(pathDatasetName(path));
            const scm = this._scmEntityLookup()?.[scmInstanceName ?? pathDatasetName(path)];
            const attribute = scm?.attributes.find(attribute => attribute.name === path.variable);
            if (attribute?.dataType === AttributeDataTypeEnum.Boolean) {
                return DEFAULT_BOOLEAN_OPTIONS;
            }
            return null;
        }
        return (
            optionset.scm.enumOptions
                ?.filter(option => (specificOptions ? specificOptions.includes(option.code) : true))
                .map(option => {
                    let { code, name } = option;
                    let description;
                    let image: ImageDefinition | undefined;
                    if (
                        optionset.optionsDef.type === FormConfigurationOptionsType.StaticOptionSet
                    ) {
                        const option = optionset.optionsDef.options.find(o => o.value === code);
                        if (option) {
                            name = this._projectTranslateService.handleLocalizedText(option.label);
                            if (option.description) {
                                description = this._projectTranslateService.handleLocalizedText(
                                    option.description
                                );
                            }
                            if (option.image) {
                                image = {
                                    src: option.image.src,
                                    alt: option.image.alt,
                                    width: option.image.width,
                                    height: option.image.height,
                                    disabled: option.image.disabled,
                                    tooltip: option.image.tooltip
                                        ? this._projectTranslateService.handleLocalizedText(
                                              option.image.tooltip
                                          )
                                        : undefined,
                                    title: option.image.title
                                        ? this._projectTranslateService.handleLocalizedText(
                                              option.image.title
                                          )
                                        : undefined,
                                    titleColor: option.image.titleColor,
                                    dotColor: option.image.dotColor
                                };
                            }
                        }
                    }
                    return {
                        value: code,
                        label: decodeHtmlEntities(name) ?? "",
                        description: decodeHtmlEntities(description ?? "") ?? "",
                        image
                    };
                }) || []
        );
    }

    getOptions$(
        path: VariablePath,
        specificOptions?: string[]
    ): Observable<SubjectVariableOption[] | null> {
        const options = this.getOptions(path, specificOptions);
        if (!options || !options.some(o => o.image)) {
            return of(options);
        }

        return combineLatest(
            options.map(option => {
                if (!option.image) {
                    return of(option);
                }

                return this.replaceImageUrl(option.image.src).pipe(
                    map(replacements => ({
                        ...option,
                        image:
                            replacements.length > 0
                                ? {
                                      ...option.image!,
                                      src: replacements.reduce(
                                          (acc, r) => acc.replace(r.match, r.replacement),
                                          option.image!.src
                                      )
                                  }
                                : option.image
                    }))
                );
            })
        );
    }

    getControlVariant(path: VariablePath): FormConfigurationOptionsDirection {
        const optionset = this._getOptionset(path);
        if (!optionset) {
            return FormConfigurationOptionsDirection.Vertical;
        }
        return optionset.optionsDef.type === FormConfigurationOptionsType.StaticOptionSet
            ? optionset.optionsDirection
            : FormConfigurationOptionsDirection.Vertical;
    }

    hasPrefillOptions(path: VariablePath): boolean {
        const prefillOptions = this._getPrefillOptionset(path);
        return (
            prefillOptions != null &&
            prefillOptions.type === FormConfigurationOptionsType.StaticOptionSet &&
            prefillOptions.options.length > 0
        );
    }

    getPrefillOptions(path: VariablePath): SubjectVariableOption[] | null {
        const prefillOptions = this._getPrefillOptionset(path);
        if (
            !prefillOptions ||
            prefillOptions.type !== FormConfigurationOptionsType.StaticOptionSet
        ) {
            return null;
        }
        return prefillOptions.options.map(option => {
            const label = this._projectTranslateService.handleLocalizedText(option.label);
            const description = option.description
                ? this._projectTranslateService.handleLocalizedText(option.description)
                : "";
            return {
                value: option.value,
                label: decodeHtmlEntities(label) ?? "",
                description: decodeHtmlEntities(description) ?? ""
            };
        });
    }

    getDynamicDefinition(path: VariablePath): FormConfigurationOptionsDefinitionDynamic | null {
        const prefillOptions = this._getPrefillOptionset(path);
        if (
            !prefillOptions ||
            prefillOptions.type !== FormConfigurationOptionsType.DynamicOptionSet
        ) {
            return null;
        }

        return prefillOptions;
    }

    defaultControlType(
        dataType: AttributeDataTypeEnum,
        restrictValues?: ReferenceDataType,
        options?: SubjectVariableOption[]
    ): DataEntryUIControlType {
        switch (restrictValues) {
            case ReferenceDataType.Enum:
                const optionsCount = options?.length ?? 0;
                if (optionsCount <= 8) {
                    return FormConfigurationElementOptionControlType.Radio;
                } else if (optionsCount < 15) {
                    return FormConfigurationElementOptionControlType.Select;
                } else {
                    return FormConfigurationElementOptionControlType.Autocomplete;
                }

            default:
                break;
        }

        switch (dataType) {
            case AttributeDataTypeEnum.Boolean:
                return FormConfigurationElementOptionControlType.Radio;
            case AttributeDataTypeEnum.Date:
                return FormConfigurationElementSimpleControlType.Date;
            case AttributeDataTypeEnum.DateTime:
            case AttributeDataTypeEnum.Timestamp:
                return FormConfigurationElementSimpleControlType.DateTime;
            case AttributeDataTypeEnum.BigInt:
            case AttributeDataTypeEnum.Integer:
            case AttributeDataTypeEnum.Short:
            case AttributeDataTypeEnum.Tiny:
                return FormConfigurationElementSimpleControlType.Integer;
            case AttributeDataTypeEnum.Decimal:
            case AttributeDataTypeEnum.Float:
            case AttributeDataTypeEnum.Double:
                return FormConfigurationElementSimpleControlType.Float;
            default:
                return FormConfigurationElementSimpleControlType.Text;
        }
    }

    instanceNameToModelName(instanceName: string): string {
        return this._scmInstanceNameMapper()?.[instanceName] ?? instanceName;
    }

    modelNameToInstanceName(modelName: string): string {
        return this._scmEntityModelNameMapper()?.[modelName] ?? modelName;
    }

    replaceImageUrl(text: string): Observable<{ match: string; replacement: string }[]> {
        const observables =
            text.match(/!\[]\((.+)\)/g)?.map(match => {
                const imageName = match.split("(")[1].split(")")[0];
                return this._getImageDefinition$(imageName).pipe(
                    filter(imageDefinition => imageDefinition !== null),
                    take(1),
                    map(imageDefinition => {
                        return {
                            match,
                            replacement: `<img src="${imageDefinition.src}" ${imageDefinition.alt ? 'alt="' + imageDefinition.alt + '"' : ""} ${imageDefinition.width ? 'width="' + imageDefinition.width + 'px"' : ""} ${imageDefinition.height ? 'height="' + imageDefinition.height + 'px"' : ""} />`
                        };
                    })
                );
            }) ?? [];
        const observables2 =
            text.match(/\.\.\/\.\.\/assets\/((.*)\.png)/g)?.map(match => {
                const imageName = match.split("assets/")[1];
                return this._getImageDefinition$(imageName).pipe(
                    filter(imageDefinition => imageDefinition !== null),
                    take(1),
                    map(imageDefinition => {
                        return {
                            match,
                            replacement: imageDefinition.src
                        };
                    })
                );
            }) ?? [];

        const observables3 =
            text.match(/overrides\/images\/(.*)\.png/g)?.map(match => {
                const imageName = match.split("overrides/images/")[1];
                return this._getImageDefinition$(imageName).pipe(
                    filter(imageDefinition => imageDefinition !== null),
                    take(1),
                    map(imageDefinition => {
                        return {
                            match,
                            replacement: imageDefinition.src
                        };
                    })
                );
            }) ?? [];
        return observables.length > 0 || observables2.length > 0 || observables3.length > 0
            ? combineLatest(observables.concat(observables2).concat(observables3))
            : of([]);
    }

    getCopySettings(datasetName: string): FormConfigurationCopySettings | undefined {
        const formConfiguration =
            this._formConfigurationLookup()?.[this.modelNameToInstanceName(datasetName)];
        if (!formConfiguration || !formConfiguration.settings?.copySettings) {
            return undefined;
        }
        return formConfiguration.settings?.copySettings;
    }

    getSectionImage(
        datasetName: string,
        states: Record<string, string>
    ): FormConfigurationImageDefinition | undefined {
        const formConfiguration =
            this._formConfigurationLookup()?.[this.modelNameToInstanceName(datasetName)];
        if (!formConfiguration || !formConfiguration.settings?.sectionImage) {
            return undefined;
        }

        const image = formConfiguration.settings?.sectionImage;
        const option = image.stateVariable ? states[image.stateVariable] : undefined;
        return option ? formConfiguration.settings?.sectionImage?.options?.[option] : undefined;
    }

    /**
     * Resolves the image-URL templates in an image's `src` to their final URLs, returning a copy
     * of the image with the replaced `src`. Falls back to the original image when resolution fails.
     */
    resolveImageSrc(
        image: FormConfigurationImageDefinition
    ): Observable<FormConfigurationImageDefinition> {
        return this.replaceImageUrl(image.src).pipe(
            map(replacements => ({
                ...image,
                src: replacements.reduce(
                    (src, replacement) => src.replace(replacement.match, replacement.replacement),
                    image.src
                )
            })),
            catchError(() => of(image))
        );
    }

    /**
     * Returns the section image for the merged/bilateral representative state (e.g. "bilateral").
     * Uses `mergedSubjects.mergedOptionKey` from the dataset config to pick the option.
     */
    getMergedSectionImage(datasetName: string): FormConfigurationImageDefinition | undefined {
        const formConfiguration =
            this._formConfigurationLookup()?.[this.modelNameToInstanceName(datasetName)];
        const mergedView = formConfiguration?.settings?.mergedSubjects;
        if (!mergedView?.mergedOptionKey) return undefined;
        const sectionImage = formConfiguration?.settings?.sectionImage;
        return sectionImage?.options?.[mergedView.mergedOptionKey];
    }

    /**
     * Ordered merged-subjects subgroups (columns): the explicit `subgroups` config when present,
     * otherwise derived from the discriminator variable's optionset so a single variable drives the
     * whole view.
     */
    getSubjectSubgroups(
        view: FormConfigurationMergedSubjectsView,
        path: DataPath
    ): FormConfigurationSubjectSubgroup[] {
        if (view.subgroups?.length) {
            return view.subgroups;
        }
        const options =
            this.getOptions({
                subjectUri: path.subjectUri,
                datasetUri: path.datasetUri,
                variable: view.discriminatorVariable
            }) ?? [];
        return options.map(option => ({
            value: option.value,
            label: [{ language: "i18n", content: option.label } as FormConfigurationLocalizedText]
        }));
    }

    /**
     * Pairs each configured subgroup with the subject whose discriminator value matches it, in
     * subgroup order; `subject` is undefined when no record exists for that subgroup. Shared
     * primitive behind merged-subject ordering, side labels and "all subgroups present" checks.
     */
    matchSubjectsToSubgroups(
        view: FormConfigurationMergedSubjectsView,
        subjects: SubjectInstance[],
        path: DataPath
    ): { subgroup: FormConfigurationSubjectSubgroup; subject?: SubjectInstance }[] {
        return this.getSubjectSubgroups(view, path).map(subgroup => ({
            subgroup,
            subject: subjects.find(
                subject => subject.variable?.[view.discriminatorVariable] === subgroup.value
            )
        }));
    }

    /**
     * Whether every configured subgroup of a merged view is represented among `subjects` (i.e. the
     * record is a complete bilateral/merged representative rather than a single side). Used to decide
     * when to show the merged label/image instead of a side-specific one.
     */
    areAllSubgroupsPresent(
        view: FormConfigurationMergedSubjectsView,
        subjects: SubjectInstance[],
        path: DataPath
    ): boolean {
        return this.matchSubjectsToSubgroups(view, subjects, path).every(
            ({ subject }) => !!subject
        );
    }

    getExternalTargetSubjects$(
        name: string,
        searchCriteria: Record<string, string>,
        optionDefinitions: FormConfigurationComplexOptionDefinition[]
    ): Observable<Record<string, string | number>[]> {
        const mappedOptions = optionDefinitions.reduce(
            (acc, option) => ((acc[option.name] = option.targetFieldRefName), acc),
            {} as Record<string, string>
        );

        return this._appSubjectApiService
            .externalOptionset$(name, searchCriteria, {
                404: {
                    lcKey: "External_optionset_not_found",
                    messageVariables: { optionset: name }
                }
            })
            .pipe(
                map(response => {
                    const options = response.options;
                    const mapped = options.map(option => {
                        const mapped: Record<string, string | number> = {};
                        for (const [wantedName, responseName] of Object.entries(mappedOptions)) {
                            if (responseName in option) {
                                mapped[wantedName] = option[responseName];
                            }
                        }
                        return mapped;
                    });

                    return mapped.map(mappedOption => {
                        Object.entries(mappedOption).forEach(([key, value]) => {
                            const sourceAttribute =
                                optionDefinitions.find(option => option.name === key)
                                    ?.sourceAttribute ?? "";
                            const datasetUri = sourceAttribute.substring(
                                0,
                                sourceAttribute.lastIndexOf(".")
                            );
                            const mappedValue = this.mapOptionToLabel(
                                datasetUri,
                                key,
                                value.toString()
                            );

                            mappedOption[key] = mappedValue ?? value.toString();
                        });

                        return mappedOption;
                    });
                })
            );
    }

    getPublishedTargetSubjects(
        projectName: string,
        datasetName: string,
        naturalKeys: Record<string, string>,
        fields: string[],
        optionDefinitions: FormConfigurationComplexOptionDefinition[]
    ): Observable<Record<string, string>[]> {
        return this._appSubjectApiService
            .publishedPatient$(projectName, datasetName, naturalKeys, fields, {
                404: {
                    lcKey: "Published_patient_not_found",
                    messageVariables: { registry: projectName, dataset: datasetName }
                }
            })
            .pipe(
                map(response =>
                    response.variables.map(subject =>
                        this._mapPublishedSubject(subject, optionDefinitions)
                    )
                )
            );
    }

    mapOptionToLabel(datasetUri: string, name: string, value: string): string | undefined {
        const fieldDefinition = this.getFieldDefinition({
            ...this._projectsService.rootPath()!,
            datasetUri,
            variable: name
        });

        if (fieldDefinition) {
            const formConfiguration =
                fieldDefinition.formConfiguration.type === FormConfigurationElementType.Control
                    ? (fieldDefinition.formConfiguration as FormConfigurationElementOptionControl)
                    : undefined;
            if (formConfiguration) {
                const optionsDef = formConfiguration.optionsDef;
                if (
                    optionsDef &&
                    optionsDef.type === FormConfigurationOptionsType.StaticOptionSet
                ) {
                    const option = optionsDef.options.find(o => o.value === value.toString());
                    if (option) {
                        return this._projectTranslateService.handleLocalizedText(option.label);
                    }
                }
            }
        }
        return undefined;
    }

    mapLabelToOption(datasetUri: string, name: string, label: string): string | undefined {
        const fieldDefinition = this.getFieldDefinition({
            ...this._projectsService.rootPath()!,
            datasetUri,
            variable: name
        });

        if (fieldDefinition) {
            const formConfiguration =
                fieldDefinition.formConfiguration.type === FormConfigurationElementType.Control
                    ? (fieldDefinition.formConfiguration as FormConfigurationElementOptionControl)
                    : undefined;
            if (formConfiguration) {
                const optionsDef = formConfiguration.optionsDef;
                if (
                    optionsDef &&
                    optionsDef.type === FormConfigurationOptionsType.StaticOptionSet
                ) {
                    const option = optionsDef.options.find(o =>
                        o.label.some(
                            l =>
                                this._projectTranslateService.handleLocalizedText(o.label) === label
                        )
                    );
                    if (option) {
                        return option.value;
                    }
                }
            }
        }
        return undefined;
    }

    private _generateScmEntityLookup(scm: Scm) {
        const scmEntityLookup: Record<string, ScmEntityContent> = {};
        const scmAttributeLookup = Object.create(null) as Record<
            string,
            Record<string, ScmEntityAttribute>
        >;
        const scmInstanceNameMapper: Record<string, string> = {};
        const scmEntityModelNameMapper: Record<string, string> = {};
        for (const entityGroup of scm.childContracts) {
            for (const entity of entityGroup.content.childContracts) {
                const instanceName = entity.instanceName;
                const modelName = entity.content.modelName;
                scmEntityLookup[instanceName] = entity.content;
                const attributesByName = Object.create(null) as Record<string, ScmEntityAttribute>;
                for (const attribute of entity.content.attributes ?? []) {
                    if (!Object.hasOwn(attributesByName, attribute.name)) {
                        attributesByName[attribute.name] = attribute;
                    }
                }
                scmAttributeLookup[instanceName] = attributesByName;
                scmInstanceNameMapper[instanceName] = modelName;
                scmInstanceNameMapper[modelName] = modelName;
                scmEntityModelNameMapper[modelName] = instanceName;
                scmEntityModelNameMapper[instanceName] = instanceName;
            }
        }
        this._scmEntityLookup.set(scmEntityLookup);
        this._scmAttributeLookup.set(scmAttributeLookup);
        this._scmInstanceNameMapper.set(scmInstanceNameMapper);
        this._scmEntityModelNameMapper.set(scmEntityModelNameMapper);
    }

    private _getOptionset(path: VariablePath): {
        scm: ScmEntityAttributeEnumExtension;
        optionsDef: FormConfigurationOptionsDefinition;
        optionsDirection: FormConfigurationOptionsDirection;
    } | null {
        if (!this.definitionsLoaded()) {
            throw new Error("Definitions not loaded");
        }
        const scmInstanceName = this.modelNameToInstanceName(pathDatasetName(path));
        const scm = this._scmEntityLookup()?.[scmInstanceName];
        const optionset = scm?.attributes.find(
            attribute => attribute.name === path.variable
        )?.enumExtension;

        const controlElement: FormConfigurationElementOptionControl | undefined =
            this.getFieldDefinition<FormConfigurationElementType.Control>(path)
                ?.formConfiguration as FormConfigurationElementOptionControl | undefined;
        if (!optionset || !controlElement) {
            return null;
        }
        return {
            scm: optionset,
            optionsDef: controlElement?.optionsDef,
            optionsDirection:
                controlElement?.optionsDirection ?? FormConfigurationOptionsDirection.Vertical
        };
    }

    private _getPrefillOptionset(path: VariablePath): FormConfigurationOptionsDefinition | null {
        const controlElement: FormConfigurationElementControlBase | undefined =
            this.getFieldDefinition<FormConfigurationElementType.Control>(path)
                ?.formConfiguration as FormConfigurationElementControlBase | undefined;
        return controlElement?.prefillOptionsDef ?? null;
    }

    private _getImageDefinition$(imageResourceName: string): Observable<ImageDefinition | null> {
        const imageDefinition = this._imageDefinitions.get(imageResourceName);
        if (imageDefinition !== undefined) {
            return of(imageDefinition);
        }

        const imageResource = this._formConfiguration()?.resources?.find(
            imageResource => imageResource.src === imageResourceName
        );
        const tooltip = imageResource?.tooltip
            ? this._projectTranslateService.handleLocalizedText(imageResource.tooltip)
            : undefined;
        const title = imageResource?.title
            ? this._projectTranslateService.handleLocalizedText(imageResource.title)
            : undefined;
        return this._imageManagerService.getImageUrl$(imageResource?.src ?? imageResourceName).pipe(
            map(src => {
                if (!src) {
                    this._imageDefinitions.set(imageResourceName, null);
                    return null;
                }
                const result: ImageDefinition = {
                    src: src,
                    alt: imageResource?.alt,
                    width: imageResource?.width,
                    height: imageResource?.height,
                    disabled: imageResource?.disabled,
                    tooltip: tooltip,
                    title: title,
                    titleColor: imageResource?.titleColor
                };
                this._imageDefinitions.set(imageResourceName, result);
                return result;
            })
        );
    }

    private _mapPublishedSubject(
        subject: Record<string, string>,
        optionDefinitions: FormConfigurationComplexOptionDefinition[]
    ): Record<string, string> {
        const result: Record<string, string> = {};
        for (const [key, value] of Object.entries(subject)) {
            const field = key.split(".").pop()!;
            const sourceAttribute =
                optionDefinitions.find(option => option.name === field)?.sourceAttribute ?? "";
            const datasetUri = sourceAttribute.substring(0, sourceAttribute.lastIndexOf("."));
            result[field] = this.mapOptionToLabel(datasetUri, field, value) ?? value;
        }
        return result;
    }

    /**
     * Returns the first `addButtonLabelOverrides` entry whose conditions are satisfied
     * against the given context (merged parent-subject variables + states), or null when
     * no override matches.
     */
    matchAddButtonOverride(
        overrides: FormConfigurationDatasetAddActionLabelOverride[] | undefined,
        context: Record<string, string | undefined>
    ): FormConfigurationDatasetAddActionLabelOverride | null {
        if (!overrides?.length) return null;
        return (
            overrides.find(o =>
                this._evaluateAddButtonConditions(o.conditions, o.conditionMode ?? "all", context)
            ) ?? null
        );
    }

    private _evaluateAddButtonConditions(
        conditions: FormConfigurationDatasetAddActionCondition[],
        mode: "any" | "all",
        context: Record<string, string | undefined>
    ): boolean {
        const evaluate = (c: FormConfigurationDatasetAddActionCondition): boolean => {
            const op = c.operation ?? "equals";
            return op === "equals" ? context[c.field] === c.value : context[c.field] !== c.value;
        };
        return mode === "any" ? conditions.some(evaluate) : conditions.every(evaluate);
    }
}
